import { DEFAULT_OPTION } from 'lib/constants';
import type { Cart, CartItem } from 'lib/burokshop/types';

/** Checkout Shoplink (paypool Burok) – ORDER_PREPARATION.md, EXEMPLES_URL_CHECKOUT_SHOPLINK.md */
const SHOPLINK_CHECKOUT_BASE = 'https://www.paylink.cool/checkout/business';

/** Racine du site Shoplink (success_url, failure_url) */
export const SHOPLINK_SITE_BASE = 'https://shoplink.boutique';

/** Webhook appelé par api.burok.app après paiement réussi */
const SHOPLINK_WEBHOOK_URL = 'https://api.burok.app/api/shoplink/webhook';

/** Délai livraison affiché sur le formulaire Order Prep (ORDER_PREPARATION copy) */
const DEFAULT_DELIVERY_ESTIMATE_TEXT = '30-45 min';
const DEFAULT_DELIVERY_ESTIMATE_MINUTES = 45;

/** Style de la page checkout (PAYLINK_PRECONFIG copy §3.1) — boutons, fond, etc. */
const DEFAULT_PAGE_STYLE = {
  primary_button_color: '#000000',
  secondary_button_color: '#F8F9FA',
  primary_button_text_color: '#FFFFFF',
  background_color: '#FFFFFF',
  header_background_color: '#F8F9FA',
  border_radius_buttons: 12
};

/** Type de marketplace pour optional_data (Kiosk / Boutique / Restaurant) */
export type ShoplinkMarketplaceType = 'Kiosk' | 'Boutique' | 'Restaurant';

/** Item du résumé de commande (orderSummary) */
interface OrderSummaryItem {
  name: string;
  description?: string;
  price: number;
  quantity: number;
  image?: string;
}

function lineToOrderItem(line: CartItem): OrderSummaryItem {
  const unitPrice =
    line.quantity > 0 ? Math.round(Number(line.cost.totalAmount.amount) / line.quantity) : 0;
  return {
    name: line.merchandise.product.title || 'Article',
    description:
      line.merchandise.title !== DEFAULT_OPTION
        ? line.merchandise.title
        : line.merchandise.product.title || '',
    price: unitPrice,
    quantity: line.quantity,
    image: line.merchandise.product.featuredImage?.url || ''
  };
}

/**
 * Groupement par store (pour affichage "1 boutique" / "plusieurs boutiques").
 */
function getStoreGroups(cart: Cart): Map<string, { storeName: string; lines: Cart['lines'] }> {
  const linesWithStore = cart.lines.filter((line) => line.storeUsername?.trim());
  const storeGroups = new Map<string, { storeName: string; lines: Cart['lines'] }>();

  for (const line of linesWithStore) {
    const username = line.storeUsername!.trim();
    const existing = storeGroups.get(username);
    const storeName = line.storeName?.trim() || username;
    if (existing) {
      existing.lines.push(line);
    } else {
      storeGroups.set(username, { storeName, lines: [line] });
    }
  }
  return storeGroups;
}

export type PayLinkCheckoutType = 'single' | 'multi' | null;

/**
 * Détermine si le panier contient un ou plusieurs marchands (pour libellé UI).
 */
export function getPayLinkCheckoutType(cart: Cart | undefined): PayLinkCheckoutType {
  if (!cart?.lines?.length) return null;
  const storeGroups = getStoreGroups(cart);
  if (storeGroups.size === 0) return cart.kioskOwnerUsername?.trim() ? 'single' : null;
  if (storeGroups.size > 1) return 'multi';
  return 'single';
}

/**
 * Construit l’URL du checkout Shoplink (paypool) avec order preparation.
 * Inclut : webhook_url, success_url, failure_url, optional_data (marketplace_type), orderPreparation=1, skipLanding=1.
 * @param cart - Panier
 * @param deliveryAmount - Livraison (0 si passage direct depuis le panier)
 * @param serviceFee - Frais de service (optionnel, défaut 0)
 * @param marketplaceType - Type pour optional_data (défaut "Kiosk")
 */
export function buildPayLinkCheckoutUrl(
  cart: Cart,
  deliveryAmount: number,
  serviceFee: number = 0,
  marketplaceType: ShoplinkMarketplaceType = 'Kiosk'
): string | null {
  if (!cart.lines.length) return null;

  const deliveryRounded = Math.round(deliveryAmount);
  const serviceFeeRounded = Math.round(serviceFee);

  const items = cart.lines.map(lineToOrderItem);
  const orderSummary = {
    items,
    delivery: deliveryRounded,
    ...(serviceFeeRounded > 0 && { service_fee: serviceFeeRounded })
  };

  const amount = Math.round(
    Number(cart.cost.subtotalAmount.amount) + deliveryRounded + serviceFeeRounded
  );

  // optional_data : lien store <-> owner + liste produits avec type (kiosk/boutique/restaurant)
  const burokUserIds = [...new Set(cart.lines.map((l) => l.storeOwnerBurokUserId).filter(Boolean))] as string[];
  const kioskIds = [...new Set(cart.lines.map((l) => l.kioskId).filter(Boolean))] as string[];

  const storeKeys = new Set<string>();
  const stores: Array<{
    kiosk_id?: string;
    boutique_id?: string;
    restaurant_id?: string;
    owner_burok_user_id: string;
    type: 'kiosk' | 'boutique' | 'restaurant';
  }> = [];
  for (const line of cart.lines) {
    const uid = line.storeOwnerBurokUserId;
    const type = line.storeType || 'kiosk';
    if (!uid) continue;
    const storeId = type === 'kiosk' ? line.kioskId : type === 'boutique' ? line.boutiqueId : line.restaurantId;
    const key = storeId ? `${type}:${storeId}` : null;
    if (key && !storeKeys.has(key)) {
      storeKeys.add(key);
      stores.push({
        ...(type === 'kiosk' && line.kioskId && { kiosk_id: line.kioskId }),
        ...(type === 'boutique' && line.boutiqueId && { boutique_id: line.boutiqueId }),
        ...(type === 'restaurant' && line.restaurantId && { restaurant_id: line.restaurantId }),
        owner_burok_user_id: uid,
        type
      });
    }
  }

  const products = cart.lines.map((line) => ({
    product_id: line.merchandise.id,
    store_type: line.storeType || ('kiosk' as const),
    ...(line.kioskId && { kiosk_id: line.kioskId }),
    ...(line.boutiqueId && { boutique_id: line.boutiqueId }),
    ...(line.restaurantId && { restaurant_id: line.restaurantId })
  }));

  const optionalData = JSON.stringify({
    marketplace_type: marketplaceType,
    burok_user_ids: burokUserIds,
    kiosk_ids: kioskIds,
    boutique_ids: [] as string[],
    restaurant_ids: [] as string[],
    stores,
    products
  });

  const params = new URLSearchParams({
    username: 'shoplink',
    shoplinkPaypool: '1',
    currency: 'FCFA',
    amount: String(amount),
    description: 'Commande Shoplink',
    layout: 'ecommerce',
    orderSummaryDisplay: 'list',
    orderPreparation: '1',
    mergeOrderPrep: '1',
    skipLanding: '1',
    delivery_estimate_text: DEFAULT_DELIVERY_ESTIMATE_TEXT,
    delivery_estimate_minutes: String(DEFAULT_DELIVERY_ESTIMATE_MINUTES),
    page_style: JSON.stringify(DEFAULT_PAGE_STYLE),
    orderSummary: JSON.stringify(orderSummary),
    webhook_url: SHOPLINK_WEBHOOK_URL,
    success_url: `${SHOPLINK_SITE_BASE}/checkout/success`,
    failure_url: `${SHOPLINK_SITE_BASE}/checkout/failed`,
    optional_data: optionalData
  });

  return `${SHOPLINK_CHECKOUT_BASE}?${params.toString()}`;
}
